// v187 admin page for outbound notification channels (Teams + future
// Slack/webhook). Sibling to AdminNotifications which keeps SMTP.
//
// Layout:
//   1. "Add Teams channel" form — name + webhook URL + events + scope
//   2. List of existing channels — toggle / test / delete per row,
//      with last-test status badges so misconfigured webhooks are
//      visible without reading the queue.

import { useState } from 'react';
import {
  MessageSquare, Plus, Send, Trash2, CheckCircle2, XCircle, Power, RotateCw,
} from 'lucide-react';
import { AdminShell } from '../../components/AdminShell';
import { Card, CardBody, CardFooter, CardHeader } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import {
  useNotificationChannels,
  createChannel,
  updateChannel,
  deleteChannel,
  testChannel,
  type NotificationChannel,
} from '../../api/notification-channels';

const KIND_LABEL: Record<NotificationChannel['kind'], string> = {
  teams: 'Microsoft Teams',
  slack: 'Slack (incoming webhook)',
  webhook: 'Generic webhook',
};

const ALL_EVENTS = [
  { key: 'block_created', label: 'New security block created' },
  { key: 'block_hit', label: 'A blocked artifact was attempted' },
];

function lastTestStatusBadge(channel: NotificationChannel) {
  if (!channel.last_test_at) {
    return <span className="text-fg-muted text-xs">never</span>;
  }
  if (channel.last_test_ok) {
    return (
      <Badge tone="success">
        <CheckCircle2 className="h-3 w-3" />
        ok
      </Badge>
    );
  }
  return (
    <Badge tone="danger" title={channel.last_error}>
      <XCircle className="h-3 w-3" />
      failed
    </Badge>
  );
}

const DEFAULT_FORM = {
  name: '',
  webhook_url: '',
  events: ['block_created', 'block_hit'] as string[],
};

// Backward-compat wrapper — see AdminNotificationsHub for the
// consolidated IA. App.tsx redirects /admin/notification-channels to
// /admin/notifications?tab=channels; this export remains for any
// direct importers.
export function AdminNotificationChannels() {
  return (
    <AdminShell
      title="Notification channels"
      description="Send security-block events to Microsoft Teams (and forthcoming Slack / generic-webhook channels). Per-user emails stay configured under Notifications. A channel here delivers a card to a chat-app webhook every time a subscribed event fires."
    >
      <AdminNotificationChannelsBody />
    </AdminShell>
  );
}

// Body for the Notifications hub — no <AdminShell> wrapper.
export function AdminNotificationChannelsBody() {
  const { data, mutate, isLoading } = useNotificationChannels();
  const [form, setForm] = useState(DEFAULT_FORM);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const channels = data?.channels ?? [];

  const onCreate = async () => {
    setBusy(true);
    setErr(null);
    setInfo(null);
    try {
      await createChannel({
        kind: 'teams',
        name: form.name,
        webhook_url: form.webhook_url,
        enabled: true,
        events: form.events,
      });
      setForm(DEFAULT_FORM);
      setInfo('Teams channel created.');
      await mutate();
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
    }
  };

  const onToggle = async (c: NotificationChannel) => {
    setBusy(true);
    setErr(null);
    try {
      await updateChannel(c.id, { enabled: !c.enabled });
      await mutate();
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
    }
  };

  const onTest = async (c: NotificationChannel) => {
    setBusy(true);
    setErr(null);
    setInfo(null);
    try {
      const r = await testChannel(c.id);
      if (r.ok) {
        setInfo(`Test card delivered to ${r.channel}.`);
      } else {
        setErr(`Test failed for ${r.channel}: ${r.error ?? 'unknown error'}`);
      }
      await mutate();
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (c: NotificationChannel) => {
    if (!confirm(`Delete channel "${c.name}"? This cannot be undone.`)) {
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await deleteChannel(c.id);
      await mutate();
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
    }
  };

  const toggleEvent = (key: string) => {
    setForm((f) => ({
      ...f,
      events: f.events.includes(key)
        ? f.events.filter((e) => e !== key)
        : [...f.events, key],
    }));
  };

  return (
    <>
      <Card>
        <CardHeader
          title="Add Teams channel"
          description="Paste an Incoming Webhook URL from your Teams channel. The first message you send will appear as a OrbitalReg card."
          actions={
            <Badge tone="neutral">
              <MessageSquare className="h-3 w-3" />
              Teams
            </Badge>
          }
        />
        <CardBody className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="teams-channel-name" className="block text-sm font-medium text-fg mb-1.5">Channel name</label>
              <Input
                id="teams-channel-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="#platform-alerts"
              />
              <p className="text-xs text-fg-muted mt-1">
                Free-form. Shown in the queue + audit log.
              </p>
            </div>
            <div>
              <label htmlFor="teams-webhook-url" className="block text-sm font-medium text-fg mb-1.5">Incoming webhook URL</label>
              <Input
                id="teams-webhook-url"
                type="url"
                value={form.webhook_url}
                onChange={(e) => setForm({ ...form, webhook_url: e.target.value })}
                placeholder="https://yourtenant.webhook.office.com/webhookb2/…"
              />
              <p className="text-xs text-fg-muted mt-1">
                Treat this URL as a secret — anyone with it can post to your channel.
              </p>
            </div>
          </div>

          <div className="border-t border-border pt-4">
            <p className="orb-section-title text-fg-muted mb-3">Events</p>
            <div className="space-y-2">
              {ALL_EVENTS.map(({ key, label }) => (
                <div key={key} className="flex items-center gap-3">
                  <input
                    id={`ev-${key}`}
                    type="checkbox"
                    checked={form.events.includes(key)}
                    onChange={() => toggleEvent(key)}
                    className="h-4 w-4"
                  />
                  <label htmlFor={`ev-${key}`} className="text-sm text-fg cursor-pointer">
                    {label}
                  </label>
                </div>
              ))}
            </div>
          </div>
        </CardBody>
        <CardFooter className="flex justify-end gap-2">
          <Button
            variant="primary"
            disabled={busy || !form.name || !form.webhook_url || form.events.length === 0}
            onClick={onCreate}
          >
            <Plus className="h-4 w-4" />
            Add channel
          </Button>
        </CardFooter>
      </Card>

      <Card>
        <CardHeader
          title={`Channels (${channels.length})`}
          description="Click Test to send a sample card. Disabled channels are skipped during fan-out."
          actions={
            <button
              type="button"
              onClick={() => mutate()}
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-sm border border-border hover:bg-bg-hover text-fg-muted hover:text-fg"
            >
              <RotateCw className="h-3.5 w-3.5" />
              Refresh
            </button>
          }
        />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="px-5 py-3 font-medium text-fg-muted">Status</th>
                <th className="px-5 py-3 font-medium text-fg-muted">Kind</th>
                <th className="px-5 py-3 font-medium text-fg-muted">Name</th>
                <th className="px-5 py-3 font-medium text-fg-muted">Webhook</th>
                <th className="px-5 py-3 font-medium text-fg-muted">Events</th>
                <th className="px-5 py-3 font-medium text-fg-muted">Last test</th>
                <th className="px-5 py-3 font-medium text-fg-muted text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={7} className="px-5 py-12 text-center text-fg-muted text-sm">
                    Loading…
                  </td>
                </tr>
              )}
              {!isLoading && channels.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-5 py-12 text-center text-fg-muted text-sm">
                    No channels yet — add one above to start sending Teams cards.
                  </td>
                </tr>
              )}
              {channels.map((c) => (
                <tr
                  key={c.id}
                  className="border-b border-border/60 last:border-0 hover:bg-bg-hover/40"
                >
                  <td className="px-5 py-3">
                    {c.enabled ? (
                      <Badge tone="success">Enabled</Badge>
                    ) : (
                      <Badge tone="neutral">Disabled</Badge>
                    )}
                  </td>
                  <td className="px-5 py-3 text-fg-muted text-xs">
                    {KIND_LABEL[c.kind] ?? c.kind}
                  </td>
                  <td className="px-5 py-3 text-fg">{c.name}</td>
                  <td className="px-5 py-3 text-fg-muted text-xs orb-code">
                    {c.webhook_url}
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex flex-wrap gap-1">
                      {c.events.length === 0 ? (
                        <Badge tone="neutral">all</Badge>
                      ) : (
                        c.events.map((ev) => (
                          <Badge key={ev} tone="neutral">{ev}</Badge>
                        ))
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    {lastTestStatusBadge(c)}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <div className="inline-flex gap-2">
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => onTest(c)}
                        className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs border border-border hover:bg-bg-hover text-fg-muted hover:text-fg"
                      >
                        <Send className="h-3.5 w-3.5" />
                        Test
                      </button>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => onToggle(c)}
                        className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs border border-border hover:bg-bg-hover text-fg-muted hover:text-fg"
                      >
                        <Power className="h-3.5 w-3.5" />
                        {c.enabled ? 'Disable' : 'Enable'}
                      </button>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => onDelete(c)}
                        className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs border border-danger/30 text-danger hover:bg-danger/10"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {err && (
        <Card className="border-danger/30 bg-danger/5">
          <CardBody>
            <p className="text-danger font-mono text-sm">{err}</p>
          </CardBody>
        </Card>
      )}

      {info && (
        <Card className="border-success/30 bg-success/5">
          <CardBody>
            <p className="text-success text-sm">{info}</p>
          </CardBody>
        </Card>
      )}
    </>
  );
}
